import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// taxRate is the *default* shown in the UI. CA + US users override this via
// their per-user customTaxRate (manual entry — combined sales tax varies by
// jurisdiction).
export function getMarketCurrency(market?: string | null): { code: string; symbol: string; locale: string; taxLabel: string; taxRate: number } {
  if (market === "au") {
    return { code: "AUD", symbol: "A$", locale: "en-AU", taxLabel: "GST", taxRate: 10 };
  }
  if (market === "ca") {
    return { code: "CAD", symbol: "C$", locale: "en-CA", taxLabel: "Sales Tax", taxRate: 0 };
  }
  if (market === "us") {
    // en-US + USD natively renders as "$1,234.56" — no post-processing needed.
    return { code: "USD", symbol: "$", locale: "en-US", taxLabel: "Sales Tax", taxRate: 0 };
  }
  return { code: "GBP", symbol: "£", locale: "en-GB", taxLabel: "VAT", taxRate: 20 };
}

export function formatMoney(value: number | string | null | undefined, market?: string | null, opts: { decimals?: number } = {}): string {
  const { code, locale } = getMarketCurrency(market);
  const num = typeof value === "string" ? Number(value) : (value ?? 0);
  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: opts.decimals ?? 2,
    maximumFractionDigits: opts.decimals ?? 2,
  }).format(isFinite(num) ? num : 0);
  // en-CA + CAD natively renders as "$1,234.56" (no country prefix), which is
  // ambiguous next to USD. Mirror the server-side `formatCurrencyForMarket`
  // post-process and force "C$" so CA users see an unambiguous currency
  // symbol throughout the UI (matches the PDF generator output). US (en-US +
  // USD) already renders the bare "$" symbol — leave untouched.
  if (market === "ca") {
    return formatted.replace(/^(\$|CA\$|CAD\s?)/, "C$");
  }
  return formatted;
}
